/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable default-param-last */
import {
  EOAccountStatus,
  EOEntityToFetch,
  EOEntityToSynchronize,
  EOJournal,
  EXACT_ONLINE_CONTACTS_SYNC_BOTH_WAYS,
  EXACT_ONLINE_CONTACTS_SYNC_METHOD_FOR_CUSTOM_VALUE,
  EXACT_ONLINE_TRANSACTIONS_SYNC_METHOD_FOR_CUSTOM_VALUE,
  ExactOnlineTransactionsSync,
  GenericExactOnlineMatching,
  IntegrationName,
  IntegrationStatus,
  LightEOAccount,
  LightEOBankEntryWithBeneficiaries,
  LightEOCostcenter,
  LightEODivision,
  LightEOGLAccount,
  LightEOSalesEntry,
  LightEOVatCode,
  createU2REntitiesFromExactOnline as createU2REntitiesFromExactOnlineMutation,
  getExactOnlineClientId as getExactOnlineClientIdMutation,
  getExactOnlineEntities as getExactOnlineEntitiesMutation,
  saveExactOnlineEntitiesMatching as saveExactOnlineEntitiesMatchingMutation,
  synchronizeExactOnline as synchronizeExactOnlineMutation,
  verifyExactOnlineIntegration as verifyExactOnlineIntegrationMutation,
} from '@rentguru/commons-utils';
import { mutation } from '@up2rent/graphql';
import { toast } from '@up2rent/ui-new';
import { createContext, useContext, useMemo, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { getCurrentEnv } from 'src/utils/envUtils';

import { useIntegrations } from './IntegrationsContext';

const getRedirectUri = () => {
  const lastPartOfPath = '/settings/integrations/details';
  if (process.env.NODE_ENV === 'development') {
    return `https://localhost.dev${lastPartOfPath}`;
  }
  const env = getCurrentEnv();
  return `https://${env}.up2rent.com${lastPartOfPath}`;
};

export const EXACT_ONLINE_REDIRECT_URI = getRedirectUri();

interface GetLightEntityExtraParams {
  avoidCalculateMinDate?: boolean;
  accountId?: string;
}

export interface ExactOnlineContext {
  getOAuthExactOnlineUrl: () => Promise<string>;
  getFirstExactOnlineTokens: (
    exactOnlineCode: string,
    transactionsSyncMethod: ExactOnlineTransactionsSync
  ) => Promise<void>;
  reconnectExactOnlineTokens: (integrationId: string, code: string) => Promise<void>;
  getEOLightAccounts: (integrationId: string, division: string, status?: EOAccountStatus) => Promise<LightEOAccount[]>;
  getEOLightCostcenters: (integrationId: string, division: string) => Promise<LightEOCostcenter[]>;
  getEOLightDivisions: (integrationId: string) => Promise<LightEODivision[]>;
  getEOLightGLAccounts: (integrationId: string, division: string) => Promise<LightEOGLAccount[]>;
  getEOLightVATCodes: (integrationId: string, division: string) => Promise<LightEOVatCode[]>;
  getEOJournals: (integrationId: string, division: string) => Promise<EOJournal[]>;
  matchDivisionsWithOwners: (matchings: GenericExactOnlineMatching[], integrationId: string) => Promise<void>;
  matchGLAccountsWithAccountLabels: (matchings: GenericExactOnlineMatching[], integrationId: string) => Promise<void>;
  matchCostcentersWithUnits: (matchings: GenericExactOnlineMatching[], integrationId: string) => Promise<void>;
  matchCustomerAccountsWithLeases: (matchings: GenericExactOnlineMatching[], integrationId: string) => Promise<void>;
  createAccountLabelsFromEOGLAccounts: (
    eoGLAccountIds: string[],
    division: string,
    integrationId: string,
    ownerId: string
  ) => Promise<void>;
  createVATRatesFromVATCodes: (
    eoVATCodeIds: string[],
    division: string,
    integrationId: string,
    ownerId: string
  ) => Promise<void>;
  synchronizeExactOnlineData: (integrationId: string, entityToSynchronize?: EOEntityToSynchronize) => Promise<void>;
  getEOLightBankEntries: (integrationId: string, divisions: string[]) => Promise<LightEOBankEntryWithBeneficiaries[]>;
  getEOLightSalesEntries: (integrationId: string, division: string, accountId: string) => Promise<LightEOSalesEntry[]>;
}

const getEntityToFetchBasedOnAccountStatus = (status: EOAccountStatus | undefined) => {
  switch (status) {
    case EOAccountStatus.CUSTOMER:
      return EOEntityToFetch.CUSTOMER_ACCOUNTS;
    default:
      return EOEntityToFetch.ACCOUNTS;
  }
};

export const ExactOnlineContext = createContext<ExactOnlineContext | null>(null);

export const ExactOnlineContextProvider = ({ children }: { children?: ReactNode }) => {
  const { createIntegration } = useIntegrations();
  const { formatMessage } = useIntl();

  const getFirstExactOnlineTokens = async (
    exactOnlineCode: string,
    transactionsSyncMethod: ExactOnlineTransactionsSync
  ) => {
    const integration = await createIntegration({
      name: IntegrationName.EXACT_ONLINE,
      status: IntegrationStatus.ACTIVE,
      configurations: [
        {
          name: EXACT_ONLINE_TRANSACTIONS_SYNC_METHOD_FOR_CUSTOM_VALUE,
          value: transactionsSyncMethod,
        },
        {
          name: EXACT_ONLINE_CONTACTS_SYNC_METHOD_FOR_CUSTOM_VALUE,
          value: EXACT_ONLINE_CONTACTS_SYNC_BOTH_WAYS,
        },
      ],
    });
    try {
      await mutation(verifyExactOnlineIntegrationMutation, {
        input: { integrationId: integration.id, code: exactOnlineCode },
      });
    } catch (_error) {
      toast.error(formatMessage({ id: 'settings.integrations.exactOnline.connectErrorSnackbar' }));
    }
  };

  const reconnectExactOnlineTokens = async (integrationId: string, code: string) => {
    try {
      await mutation(verifyExactOnlineIntegrationMutation, {
        input: { integrationId, code },
      });
    } catch (_error) {
      toast.error(formatMessage({ id: 'settings.integrations.exactOnline.connectErrorSnackbar' }));
    }
  };

  const getOAuthExactOnlineUrl = async () => {
    const exactOnlineClientId = await getExactOnlineClientId();
    const oauthUrl = new URL('https://start.exactonline.be/api/oauth2/auth');
    oauthUrl.searchParams.set('client_id', exactOnlineClientId);
    oauthUrl.searchParams.set('redirect_uri', EXACT_ONLINE_REDIRECT_URI);
    oauthUrl.searchParams.set('response_type', 'code');
    return oauthUrl.toString();
  };

  const getExactOnlineClientId = async () => {
    const clientId = await mutation<string>(getExactOnlineClientIdMutation, {
      input: { placeholder: null },
    });
    return clientId;
  };

  const getEOLightAccounts = async (integrationId: string, division: string, status?: EOAccountStatus) => {
    const allLightAccounts = await getLightEntityFromExactOnline(
      integrationId,
      getEntityToFetchBasedOnAccountStatus(status),
      division
    );
    if (status) {
      return allLightAccounts?.filter((lightAccount: LightEOAccount) => lightAccount.Status === status);
    }
    return allLightAccounts;
  };

  const getEOLightCostcenters = async (integrationId: string, division: string) =>
    await getLightEntityFromExactOnline(integrationId, EOEntityToFetch.COST_CENTERS, division);

  const getEOLightDivisions = async (integrationId: string) =>
    await getLightEntityFromExactOnline(integrationId, EOEntityToFetch.DIVISIONS);

  const getEOLightGLAccounts = async (integrationId: string, division: string) =>
    await getLightEntityFromExactOnline(integrationId, EOEntityToFetch.GLACCOUNTS, division);

  const getEOLightVATCodes = async (integrationId: string, division: string) =>
    await getLightEntityFromExactOnline(integrationId, EOEntityToFetch.VAT_CODES, division);

  const getEOJournals = async (integrationId: string, division: string) =>
    await getLightEntityFromExactOnline(integrationId, EOEntityToFetch.JOURNALS, division);

  const sleep = async (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const getLightEntityFromExactOnline = async (
    integrationId: string,
    eOEntityToFetch: EOEntityToFetch,
    division?: string,
    extraParams?: GetLightEntityExtraParams
  ) => {
    const nbRetryMax = 3;
    const nbMsBetweenEachTry = 5000;
    let nbRetry = 0;
    while (nbRetryMax !== nbRetry) {
      try {
        const result = await mutation<string>(getExactOnlineEntitiesMutation, {
          input: {
            integrationId,
            entity: eOEntityToFetch,
            division: division ?? null,
            avoidCalculateMinDate: extraParams?.avoidCalculateMinDate ?? null,
            accountId: extraParams?.accountId ?? null,
          },
        });
        const data = JSON.parse(result);
        return data[eOEntityToFetch] ?? [];
      } catch (error) {
        console.error('Error with Exact Online Sync', error);
        nbRetry += 1;
        await sleep(nbMsBetweenEachTry);
      }
    }
    return [];
  };

  const matchDivisionsWithOwners = async (matchings: GenericExactOnlineMatching[], integrationId: string) =>
    await matchEntityFromExactOnline(matchings, EOEntityToFetch.DIVISIONS, integrationId);

  const matchGLAccountsWithAccountLabels = async (matchings: GenericExactOnlineMatching[], integrationId: string) =>
    await matchEntityFromExactOnline(matchings, EOEntityToFetch.GLACCOUNTS, integrationId);

  const matchCostcentersWithUnits = async (matchings: GenericExactOnlineMatching[], integrationId: string) =>
    await matchEntityFromExactOnline(matchings, EOEntityToFetch.COST_CENTERS, integrationId);

  const matchCustomerAccountsWithLeases = async (matchings: GenericExactOnlineMatching[], integrationId: string) =>
    await matchEntityFromExactOnline(matchings, EOEntityToFetch.CUSTOMER_ACCOUNTS, integrationId);

  const matchEntityFromExactOnline = async (
    matching: GenericExactOnlineMatching[],
    eOEntityToFetch: EOEntityToFetch,
    integrationId: string
  ) => {
    try {
      const result = await mutation<string>(saveExactOnlineEntitiesMatchingMutation, {
        input: { integrationId, entity: eOEntityToFetch, matching: JSON.stringify(matching) },
      });
      const data = JSON.parse(result);
      return data[eOEntityToFetch];
    } catch (error) {
      console.error('Error with Exact Online Matching', error);
    }
  };

  const synchronizeExactOnlineData = async (integrationId: string, entityToSynchronize?: EOEntityToSynchronize) => {
    try {
      await mutation(synchronizeExactOnlineMutation, {
        input: { integrationId, entityToSynchronize: entityToSynchronize ?? null },
      });
    } catch (error) {
      console.error('Error with Exact Online Sync', error);
    }
  };

  const createAccountLabelsFromEOGLAccounts = async (
    eoGLAccountIds: string[],
    division: string,
    integrationId: string,
    ownerId: string
  ) =>
    await createU2REntitiesFromExactOnline(
      eoGLAccountIds,
      EOEntityToFetch.GLACCOUNTS,
      integrationId,
      division,
      ownerId
    );

  const createVATRatesFromVATCodes = async (
    eoVATCodeIds: string[],
    division: string,
    integrationId: string,
    ownerId: string
  ) =>
    await createU2REntitiesFromExactOnline(eoVATCodeIds, EOEntityToFetch.VAT_CODES, integrationId, division, ownerId);

  const createU2REntitiesFromExactOnline = async (
    exactOnlineIds: string[],
    eOEntityToFetch: EOEntityToFetch,
    integrationId: string,
    division: string,
    ownerId: string
  ) => {
    try {
      const result = await mutation<string>(createU2REntitiesFromExactOnlineMutation, {
        input: {
          integrationId,
          entity: eOEntityToFetch,
          exactOnlineIds: JSON.stringify(exactOnlineIds),
          division,
          ownerId,
        },
      });
      return JSON.parse(result);
    } catch (error) {
      console.error('Error with Exact Online data creation', error);
    }
  };

  const getEOLightBankEntries = async (
    integrationId: string,
    divisions: string[]
  ): Promise<LightEOBankEntryWithBeneficiaries[]> =>
    await getLightEntityFromExactOnline(integrationId, EOEntityToFetch.BANK_ENTRIES, divisions[0], {
      avoidCalculateMinDate: true,
    });

  const getEOLightSalesEntries = async (
    integrationId: string,
    division: string,
    accountId: string
  ): Promise<LightEOSalesEntry[]> =>
    await getLightEntityFromExactOnline(integrationId, EOEntityToFetch.SALES_ENTRIES, division, { accountId });

  const values = useMemo(
    () => ({
      getOAuthExactOnlineUrl,
      getFirstExactOnlineTokens,
      reconnectExactOnlineTokens,
      getEOLightDivisions,
      getEOLightGLAccounts,
      getEOLightVATCodes,
      getEOJournals,
      getEOLightCostcenters,
      getEOLightAccounts,
      matchDivisionsWithOwners,
      matchGLAccountsWithAccountLabels,
      matchCostcentersWithUnits,
      matchCustomerAccountsWithLeases,
      createAccountLabelsFromEOGLAccounts,
      createVATRatesFromVATCodes,
      synchronizeExactOnlineData,
      getEOLightBankEntries,
      getEOLightSalesEntries,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return <ExactOnlineContext.Provider value={values}>{children}</ExactOnlineContext.Provider>;
};

export const useExactOnline = (): ExactOnlineContext => {
  const context = useContext<ExactOnlineContext | null>(ExactOnlineContext);

  if (!context) {
    throw new Error('`useExactOnline` hook must be used within a `ExactOnlineContext` component');
  }

  return context;
};
